import { GridApi } from 'ag-grid-enterprise';
import { getIsNotNullable } from 'src/app/core/nullable-utils';
import { InterfaceItemDto } from 'src/app/core/services/deliverable-services/items.service';
import { PropertyDefDataType } from 'src/app/core/services/property-definitions-api.service';
import { extractIxValue } from 'src/app/shared/ix-grid/shared/utils/helpers';
import { IdPair } from '../comments-panel/comments-panel-selection';
import { DeliverablesGridDataType } from '../ix-grid/deliverables-grid/utils/deliverables-grid-interfaces';
import { IndexHeader, PropertyDefAsIndexHeader } from '../models/index-header';

/**
 * Finds property items from array of registerItemId and propertyDefId.
 * @property {RegisterItemProperty[]} propertyItems
 * @property {IdPair[]} items
 * @returns - RegisterItemProperty[]
 */
export const findPropertyItems = <T extends { registerItemId?: number; propertyDefId?: number }>(
  propertyItems: T[] = [],
  items: Partial<IdPair>[] = [],
): T[] => {
  const isItemSelection = !items[0]?.propertyDefId;
  return propertyItems.filter(({ registerItemId, propertyDefId }) => {
    return items.some(item => {
      const compareProperties = isItemSelection || propertyDefId === item.propertyDefId;
      return Number(registerItemId) === Number(item.registerItemId) && compareProperties;
    });
  });
};

export const findCommentableProperties = <T extends { propertyDefId?: number }>(
  propertySelection: T[],
  propertyDefs: PropertyDefAsIndexHeader[],
): T[] => {
  return propertySelection.filter(({ propertyDefId }) => isCommentableProperty(propertyDefs, propertyDefId));
};

const isCommentableProperty = (propertyDefs: PropertyDefAsIndexHeader[], propertyDefId?: number) => {
  return propertyDefs.find(({ id }) => id === propertyDefId)?.commentable;
};

export const isDeliverableId = (datatype?: DeliverablesGridDataType) => datatype === 'LABEL_NUMBER';
export const isIndexName = (datatype?: PropertyDefDataType) => datatype === 'INDEX_REF';
export const isDeliverableType = (datatype?: PropertyDefDataType) => datatype === 'DELIVERABLE_TYPE_REF';

/*
 * Find column datatype from [propertyDefId]
 * As deliverable and property definition datatypes are different
 */
export const columnDatatype = (propertyDefs: IndexHeader[], propertyDefId: number) =>
  propertyDefs.find(({ id }) => id === propertyDefId)?.datatype;

export const getCommentableValues = (
  tableData: InterfaceItemDto[],
  selectedProperties: IdPair[],
  propertyDefs: PropertyDefAsIndexHeader[],
  api: GridApi,
): string[] => {
  const propertyItems = tableData.flatMap(({ properties }) => properties).filter(getIsNotNullable);

  const propertyList = findPropertyItems(propertyItems, selectedProperties);
  const commentableProperties = findCommentableProperties(propertyList, propertyDefs);

  return commentableProperties
    .map(({ registerItemId, propertyDefId }) => {
      if (!registerItemId || !propertyDefId) {
        return;
      }
      const row = api.getRowNode(registerItemId.toString());
      return row ? api.getCellValue({ colKey: propertyDefId.toString(), rowNode: row }) : undefined;
    })
    .map(extractIxValue); // Extracts the true cell value when we are showing the Comos value as well
};
